import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Music, Search, Plus, X, Calendar, RefreshCw, Loader2,
  Bookmark, BookmarkCheck, TrendingUp, Radio, Filter, Disc,
  Film, Star, Clapperboard, Heart, Play, Clock, DollarSign, Users, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../context/UserAuthContext';

const API_BASE = window.location.origin;

const GENRES = [
  { id: 'all',       label: 'All Genres',  color: '#9CA3AF' },
  { id: 'hip-hop',   label: 'Hip Hop',     color: '#8B5CF6' },
  { id: 'r-n-b',     label: 'R&B / Soul',  color: '#EC4899' },
  { id: 'pop',       label: 'Pop',         color: '#06B6D4' },
  { id: 'rock',      label: 'Rock',        color: '#F59E0B' },
  { id: 'electronic',label: 'Electronic',  color: '#10B981' },
  { id: 'country',   label: 'Country',     color: '#D97706' },
  { id: 'latin',     label: 'Latin',       color: '#EF4444' },
  { id: 'metal',     label: 'Metal',       color: '#6B7280' },
  { id: 'jazz',      label: 'Jazz',        color: '#A78BFA' },
  { id: 'classical', label: 'Classical',   color: '#FCD34D' },
  { id: 'other',     label: 'Other',       color: '#4B5563' },
];
const GENRE_MAP = Object.fromEntries(GENRES.map(g => [g.id, g]));

const TYPE_COLORS = {
  album:  'bg-purple-500/20 text-purple-300 border-purple-500/30',
  single: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  ep:     'bg-pink-500/20 text-pink-300 border-pink-500/30',
  other:  'bg-gray-700/50 text-gray-400 border-gray-600/30',
};

const DATE_PRESETS = [
  { id: '2weeks', label: '2 Weeks',    days: 14 },
  { id: 'month',  label: 'This Month', days: 30 },
  { id: '60',     label: '2 Months',   days: 60 },
  { id: '90',     label: '3 Months',   days: 90 },
];

function fmt(dateStr) {
  if (!dateStr) return '?';
  try { return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return dateStr; }
}

function countdown(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const rel = new Date(dateStr + 'T00:00:00');
  const diff = Math.round((rel - today) / 86400000);
  if (diff === 0) return { label: 'Today', cls: 'bg-purple-500/30 text-purple-200' };
  if (diff === 1) return { label: 'Tomorrow', cls: 'bg-cyan-500/20 text-cyan-300' };
  if (diff < 0)  return { label: `${Math.abs(diff)}d ago`, cls: 'bg-gray-800 text-gray-500' };
  return { label: `in ${diff}d`, cls: 'bg-gray-800 text-gray-400' };
}

// Artwork cache to avoid refetching on re-mount
const artworkCache = {};

// Movie genre colors
const MOVIE_GENRE_COLORS = {
  'Action': '#EF4444', 'Adventure': '#F97316', 'Animation': '#06B6D4', 'Comedy': '#FBBF24',
  'Crime': '#6B7280', 'Documentary': '#10B981', 'Drama': '#8B5CF6', 'Family': '#EC4899',
  'Fantasy': '#A78BFA', 'History': '#D97706', 'Horror': '#DC2626', 'Music': '#8B5CF6',
  'Mystery': '#6366F1', 'Romance': '#F472B6', 'Science Fiction': '#22D3EE', 'TV Movie': '#9CA3AF',
  'Thriller': '#EF4444', 'War': '#78716C', 'Western': '#B45309',
};

const MovieCard = React.memo(function MovieCard({ movie, isFavorite, onFavorite, onUnfavorite, onCardClick }) {
  const cd = countdown(movie.release_date);

  const handleHeartClick = (e) => {
    e.stopPropagation();
    if (isFavorite) {
      onUnfavorite(movie);
    } else {
      onFavorite(movie);
    }
  };

  const handleCardClick = () => {
    if (onCardClick) onCardClick(movie);
  };

  return (
    <div
      className="bg-black border border-gray-400 rounded-xl overflow-hidden group flex flex-col hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 cursor-pointer"
      data-testid={`movie-card-${movie.id}`}
      onClick={handleCardClick}
    >
      <div className="relative w-full aspect-[2/3] bg-[#0a0a0a] overflow-hidden">
        {movie.poster ? (
          <img src={movie.poster} alt={movie.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-black">
            <Clapperboard size={36} className="text-gray-600" />
            <span className="text-xs text-gray-600 px-2 text-center line-clamp-2">{movie.title}</span>
          </div>
        )}
        {cd && (
          <span className={`absolute top-2 left-2 text-xs font-semibold px-2 py-0.5 rounded-full ${cd.cls}`}>
            {cd.label}
          </span>
        )}
        {movie.rating > 0 && (
          <span className="absolute top-2 right-2 flex items-center gap-1 text-xs font-bold bg-black/70 backdrop-blur-sm px-2 py-0.5 rounded-full text-yellow-400">
            <Star size={10} className="fill-yellow-400" />{movie.rating}
          </span>
        )}
        {/* Play icon overlay on hover */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Play size={24} className="text-white fill-white ml-1" />
          </div>
        </div>
        {/* Heart/Favorite button */}
        <button
          onClick={handleHeartClick}
          className={`absolute bottom-2 right-2 p-1.5 rounded-full transition-all z-10 ${
            isFavorite 
              ? 'bg-red-500/90 text-white' 
              : 'bg-black/60 backdrop-blur-sm text-gray-400 hover:text-red-400 hover:bg-black/80'
          }`}
          data-testid={`favorite-btn-${movie.id}`}
        >
          <Heart size={14} className={isFavorite ? 'fill-white' : ''} />
        </button>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-1.5">
        <p className="font-semibold text-white text-sm leading-snug line-clamp-2">{movie.title}</p>
        {movie.overview && (
          <p className="text-gray-500 text-xs line-clamp-2">{movie.overview}</p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap mt-auto pt-1">
          {movie.genres?.slice(0, 2).map(g => (
            <span key={g} className="text-xs px-1.5 py-0.5 rounded font-medium"
              style={{ backgroundColor: (MOVIE_GENRE_COLORS[g] || '#4B5563') + '25', color: MOVIE_GENRE_COLORS[g] || '#9CA3AF' }}>
              {g}
            </span>
          ))}
          <span className="text-xs text-gray-500 ml-auto flex items-center gap-0.5">
            <Calendar size={10} />{fmt(movie.release_date)}
          </span>
        </div>
      </div>
    </div>
  );
});

// Movie Detail Modal Component
const MovieDetailModal = React.memo(function MovieDetailModal({ movie, onClose, isFavorite, onFavorite, onUnfavorite }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!movie) return;
    setLoading(true);
    setError(null);
    
    fetch(`${API_BASE}/api/movies/details/${movie.id}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load movie details');
        return res.json();
      })
      .then(data => {
        setDetails(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [movie]);

  // Close on escape key
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!movie) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleFavoriteClick = () => {
    if (isFavorite) {
      onUnfavorite(movie);
    } else {
      onFavorite(movie);
    }
  };

  const formatRuntime = (mins) => {
    if (!mins) return null;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const formatMoney = (amount) => {
    if (!amount) return null;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      data-testid="movie-detail-modal"
    >
      <div className="bg-[#0a0a0a] border border-gray-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header with backdrop */}
        <div className="relative">
          {details?.backdrop ? (
            <div className="w-full h-48 md:h-64 overflow-hidden">
              <img src={details.backdrop} alt="" className="w-full h-full object-cover opacity-50" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent" />
            </div>
          ) : (
            <div className="w-full h-32 bg-gradient-to-b from-gray-900 to-[#0a0a0a]" />
          )}
          
          {/* Close button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-black/60 backdrop-blur-sm rounded-full text-gray-400 hover:text-white hover:bg-black/80 transition-all"
            data-testid="close-modal-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 -mt-20 relative">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={32} className="animate-spin text-red-400" />
            </div>
          ) : error ? (
            <div className="text-center py-20 text-gray-400">
              <p>Failed to load movie details</p>
              <button onClick={onClose} className="mt-4 text-red-400 hover:underline">Close</button>
            </div>
          ) : details ? (
            <div className="flex flex-col md:flex-row gap-6">
              {/* Poster */}
              <div className="flex-shrink-0 w-40 md:w-52">
                {details.poster ? (
                  <img src={details.poster} alt={details.title} className="w-full rounded-xl shadow-2xl" />
                ) : (
                  <div className="w-full aspect-[2/3] bg-gray-900 rounded-xl flex items-center justify-center">
                    <Clapperboard size={48} className="text-gray-700" />
                  </div>
                )}
                
                {/* Favorite button */}
                <button
                  onClick={handleFavoriteClick}
                  className={`w-full mt-3 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                    isFavorite 
                      ? 'bg-red-500 text-white hover:bg-red-600' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                  data-testid="modal-favorite-btn"
                >
                  <Heart size={16} className={isFavorite ? 'fill-white' : ''} />
                  {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                </button>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">{details.title}</h2>
                {details.tagline && (
                  <p className="text-gray-400 italic mb-3">"{details.tagline}"</p>
                )}

                {/* Meta row */}
                <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
                  {details.rating > 0 && (
                    <span className="flex items-center gap-1 text-yellow-400 font-semibold">
                      <Star size={14} className="fill-yellow-400" />
                      {details.rating}/10
                      <span className="text-gray-500 font-normal">({details.vote_count?.toLocaleString()} votes)</span>
                    </span>
                  )}
                  {details.release_date && (
                    <span className="flex items-center gap-1 text-gray-400">
                      <Calendar size={14} />
                      {fmt(details.release_date)}
                    </span>
                  )}
                  {details.runtime > 0 && (
                    <span className="flex items-center gap-1 text-gray-400">
                      <Clock size={14} />
                      {formatRuntime(details.runtime)}
                    </span>
                  )}
                  {details.status && details.status !== 'Released' && (
                    <span className="px-2 py-0.5 bg-red-500/20 text-red-300 rounded text-xs font-medium">
                      {details.status}
                    </span>
                  )}
                </div>

                {/* Genres */}
                {details.genres?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {details.genres.map(g => (
                      <span key={g} className="text-xs px-2 py-1 rounded-full font-medium"
                        style={{ backgroundColor: (MOVIE_GENRE_COLORS[g] || '#4B5563') + '30', color: MOVIE_GENRE_COLORS[g] || '#9CA3AF' }}>
                        {g}
                      </span>
                    ))}
                  </div>
                )}

                {/* Overview */}
                {details.overview && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-400 mb-1">Overview</h3>
                    <p className="text-gray-300 text-sm leading-relaxed">{details.overview}</p>
                  </div>
                )}

                {/* Director */}
                {details.director && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-400 mb-1">Director</h3>
                    <p className="text-white">{details.director.name}</p>
                  </div>
                )}

                {/* Cast */}
                {details.cast?.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-400 mb-2">Cast</h3>
                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                      {details.cast.slice(0, 6).map(c => (
                        <div key={c.id} className="flex-shrink-0 text-center w-16">
                          {c.profile_path ? (
                            <img src={c.profile_path} alt={c.name} className="w-14 h-14 rounded-full object-cover mx-auto mb-1" />
                          ) : (
                            <div className="w-14 h-14 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-1">
                              <Users size={20} className="text-gray-600" />
                            </div>
                          )}
                          <p className="text-xs text-white truncate">{c.name}</p>
                          <p className="text-xs text-gray-500 truncate">{c.character}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Budget & Revenue */}
                {(details.budget > 0 || details.revenue > 0) && (
                  <div className="flex gap-6 mb-4">
                    {details.budget > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-gray-500">Budget</h3>
                        <p className="text-white flex items-center gap-1"><DollarSign size={14} className="text-green-400" />{formatMoney(details.budget)}</p>
                      </div>
                    )}
                    {details.revenue > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-gray-500">Revenue</h3>
                        <p className="text-white flex items-center gap-1"><DollarSign size={14} className="text-green-400" />{formatMoney(details.revenue)}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Production companies */}
                {details.production_companies?.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-xs font-semibold text-gray-500 mb-1">Production</h3>
                    <p className="text-gray-400 text-sm">{details.production_companies.join(' • ')}</p>
                  </div>
                )}

                {/* External links */}
                <div className="flex gap-3">
                  {details.imdb_id && (
                    <a href={`https://www.imdb.com/title/${details.imdb_id}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-yellow-400 hover:underline">
                      <ExternalLink size={14} /> IMDb
                    </a>
                  )}
                  {details.homepage && (
                    <a href={details.homepage} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-blue-400 hover:underline">
                      <ExternalLink size={14} /> Official Site
                    </a>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Trailer section */}
          {details?.trailer && (
            <div className="mt-6 pt-6 border-t border-gray-800">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Play size={18} className="text-red-400" />
                {details.trailer.official ? 'Official Trailer' : details.trailer.name || 'Trailer'}
              </h3>
              <div className="aspect-video rounded-xl overflow-hidden bg-black">
                <iframe
                  src={details.trailer.url}
                  title={details.trailer.name || 'Movie Trailer'}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const ReleaseCard = React.memo(function ReleaseCard({ release, isWatched, onWatch, onUnwatch, genreInfo }) {
  const [imgSrc, setImgSrc] = useState(() => artworkCache[release.id] || release.cover_art || null);
  const [imgErr, setImgErr] = useState(false);
  const [artworkLoaded, setArtworkLoaded] = useState(!!artworkCache[release.id]);
  const fetchedRef = useRef(false);
  const cd = countdown(release.release_date);
  const typeCls = TYPE_COLORS[release.type] || TYPE_COLORS.other;

  // Lazy artwork fetch — only if no cover_art and not already cached
  useEffect(() => {
    if (fetchedRef.current || imgSrc || release.cover_art) return;
    fetchedRef.current = true;

    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetch(
        `${API_BASE}/api/music-releases/artwork?artist=${encodeURIComponent(release.artist)}&title=${encodeURIComponent(release.title)}`,
        { signal: controller.signal }
      )
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d?.url) {
            artworkCache[release.id] = d.url;
            setImgSrc(d.url);
          } else {
            setImgErr(true);
          }
        })
        .catch(() => {});
    }, Math.random() * 2000); // Stagger requests over 2s to avoid flooding

    return () => { controller.abort(); clearTimeout(timer); };
  }, [release.id, release.artist, release.title, release.cover_art, imgSrc]);

  const handleImgError = () => {
    if (artworkLoaded) { setImgErr(true); return; }
    setArtworkLoaded(true);
    // Try iTunes fallback once
    fetch(`${API_BASE}/api/music-releases/artwork?artist=${encodeURIComponent(release.artist)}&title=${encodeURIComponent(release.title)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.url) { artworkCache[release.id] = d.url; setImgSrc(d.url); setImgErr(false); }
        else setImgErr(true);
      })
      .catch(() => setImgErr(true));
  };

  return (
    <div
      className="bg-black border border-gray-400 rounded-xl overflow-hidden group flex flex-col hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300"
      style={cd?.label === 'Today' ? { borderColor: 'rgba(168,85,247,0.55)', boxShadow: '0 0 18px rgba(168,85,247,0.25)' } : {}}
      data-testid={`release-card-${release.id}`}
    >
      <div className="relative w-full aspect-square bg-[#0a0a0a] overflow-hidden">
        {imgSrc && !imgErr ? (
          <img src={imgSrc} alt={release.title} className="w-full h-full object-cover" onError={handleImgError} loading="lazy" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-black">
            <Disc size={36} className="text-gray-600" />
            <span className="text-xs text-gray-600 px-2 text-center line-clamp-2">{release.artist}</span>
          </div>
        )}
        {cd && (
          <span className={`absolute top-2 left-2 text-xs font-semibold px-2 py-0.5 rounded-full ${cd.cls}`}>
            {cd.label}
          </span>
        )}
        <button
          onClick={() => isWatched ? onUnwatch(release.artist) : onWatch(release)}
          className={`absolute top-2 right-2 p-1.5 rounded-full backdrop-blur-sm transition-all ${
            isWatched ? 'bg-yellow-500/20 text-yellow-400' : 'bg-black/40 text-gray-400 hover:text-yellow-400 opacity-0 group-hover:opacity-100'
          }`}
          title={isWatched ? 'Remove from watchlist' : 'Watch this artist'}
          data-testid={`watch-btn-${release.id}`}
        >
          {isWatched ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
        </button>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-1.5">
        <p className="font-semibold text-white text-sm leading-snug line-clamp-2">{release.title}</p>
        <p className="text-gray-400 text-xs truncate">{release.artist}</p>
        <div className="flex items-center gap-1.5 flex-wrap mt-auto pt-1">
          <span className={`text-xs px-1.5 py-0.5 rounded border capitalize ${typeCls}`}>{release.type}</span>
          {genreInfo && (
            <span className="text-xs px-1.5 py-0.5 rounded font-medium"
              style={{ backgroundColor: genreInfo.color + '25', color: genreInfo.color }}>
              {genreInfo.label}
            </span>
          )}
          <span className="text-xs text-gray-500 ml-auto flex items-center gap-0.5">
            <Calendar size={10} />{fmt(release.release_date)}
          </span>
        </div>
      </div>
    </div>
  );
});

export default function MusicReleases() {
  const userId = useUserId();

  const [mediaType, setMediaType]   = useState('music'); // 'music' | 'movies'
  const [tab, setTab]               = useState('upcoming');
  const [releases, setReleases]     = useState([]);
  const [movies, setMovies]         = useState([]);
  const [watchlist, setWatchlist]    = useState([]);
  const [movieFavorites, setMovieFavorites] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [classifying, setClassifying] = useState(false);
  const [genreMap, setGenreMap]     = useState({});
  
  // Movie detail modal
  const [selectedMovie, setSelectedMovie] = useState(null);

  const [search, setSearch]         = useState('');
  const [activeGenres, setActiveGenres] = useState(['all']);
  const [datePreset, setDatePreset] = useState('2weeks');
  const [typeFilter, setTypeFilter] = useState('all');

  const [artistQuery, setArtistQuery]       = useState('');
  const [artistResults, setArtistResults]   = useState([]);
  const [artistSearching, setArtistSearching] = useState(false);
  const [showArtistSearch, setShowArtistSearch] = useState(false);

  const searchTimer = useRef(null);
  const abortRef = useRef(null);

  // Pagination
  const [visibleCount, setVisibleCount] = useState(30);
  const sentinelRef = useRef(null);

  // Fetch movies
  const fetchMovies = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setVisibleCount(30);
    try {
      const endpoint = tab === 'upcoming' ? 'upcoming' : 'now-playing';
      const res = await fetch(
        `${API_BASE}/api/movies/${endpoint}`,
        { signal: controller.signal }
      );
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      setMovies(data.movies || []);
    } catch (e) {
      if (e.name !== 'AbortError') toast.error('Could not load movies');
    } finally { setLoading(false); }
  }, [tab]);

  // Fetch releases with AbortController to prevent stale updates
  const fetchReleases = useCallback(async () => {
    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setVisibleCount(30);
    try {
      const days = DATE_PRESETS.find(p => p.id === datePreset)?.days || 14;
      const endpoint = tab === 'upcoming' ? 'upcoming' : 'recent';
      
      // Build query params
      const params = new URLSearchParams({
        days: days.toString(),
        type_filter: typeFilter,
        limit: '200'
      });
      
      // Add genre filter if selected (for upcoming endpoint with hip-hop or r-n-b)
      const isAllGenres = activeGenres.includes('all') || activeGenres.length === 0;
      if (!isAllGenres && tab === 'upcoming') {
        // If hip-hop or r-n-b is the only selected genre, filter server-side
        if (activeGenres.length === 1 && ['hip-hop', 'r-n-b'].includes(activeGenres[0])) {
          params.append('genre_filter', activeGenres[0]);
        }
      }
      
      const res = await fetch(
        `${API_BASE}/api/music-releases/${endpoint}?${params.toString()}`,
        { signal: controller.signal }
      );
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      const list = data.releases || [];
      setReleases(list);
      setLoading(false);

      // Classify genres in the background — cap at 60 artists to avoid heavy requests
      const unique = [...new Set(list.map(r => r.artist).filter(Boolean))].slice(0, 60);
      if (unique.length > 0) {
        setClassifying(true);
        try {
          const gr = await fetch(`${API_BASE}/api/music-releases/classify-genres`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(unique),
            signal: controller.signal,
          });
          if (gr.ok) {
            const gd = await gr.json();
            setGenreMap(prev => ({ ...prev, ...(gd.genres || {}) }));
          }
        } catch (e) {
          if (e.name !== 'AbortError') console.error('Genre classification failed:', e);
        } finally { setClassifying(false); }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        toast.error('Could not load releases');
        setLoading(false);
      }
    }
  }, [tab, datePreset, typeFilter, activeGenres]);

  const fetchWatchlist = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_BASE}/api/music-releases/watchlist?user_id=${encodeURIComponent(userId)}`);
      if (res.ok) { const d = await res.json(); setWatchlist(d.watchlist || []); }
    } catch { /* ignored */ }
  }, [userId]);

  // Fetch movie favorites
  const fetchMovieFavorites = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_BASE}/api/movies/favorites?user_id=${encodeURIComponent(userId)}`);
      if (res.ok) { const d = await res.json(); setMovieFavorites(d.favorites || []); }
    } catch { /* ignored */ }
  }, [userId]);

  useEffect(() => { 
    if (mediaType === 'music') fetchReleases(); 
    else if (tab !== 'favorites') fetchMovies(); 
  }, [mediaType, fetchReleases, fetchMovies, tab]);
  useEffect(() => { if (userId) fetchWatchlist(); }, [userId, fetchWatchlist]);
  useEffect(() => { if (userId && mediaType === 'movies') fetchMovieFavorites(); }, [userId, mediaType, fetchMovieFavorites]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  // Infinite scroll — load more cards as user scrolls
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisibleCount(prev => prev + 30); },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading]);

  // Artist search with debounce
  useEffect(() => {
    if (!artistQuery.trim()) { setArtistResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setArtistSearching(true);
      try {
        const res = await fetch(`${API_BASE}/api/music-releases/artist-search?q=${encodeURIComponent(artistQuery)}`);
        if (res.ok) { const d = await res.json(); setArtistResults(d.artists || []); }
      } catch { /* ignored */ } finally { setArtistSearching(false); }
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [artistQuery]);

  // Watchlist actions
  const addToWatchlist = useCallback(async (release) => {
    if (!userId) { toast.error('Please log in'); return; }
    try {
      const res = await fetch(`${API_BASE}/api/music-releases/watchlist?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artist_name: release.artist, artist_mbid: release.artist_mbid }),
      });
      if (res.ok) { const d = await res.json(); setWatchlist(p => [d.item, ...p]); toast.success(`Watching ${release.artist}`); }
      else if (res.status === 409) toast.info(`${release.artist} already in watchlist`);
    } catch { toast.error('Failed to add'); }
  }, [userId]);

  const addArtistToWatchlist = useCallback(async (artist) => {
    if (!userId) { toast.error('Please log in'); return; }
    try {
      const res = await fetch(`${API_BASE}/api/music-releases/watchlist?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artist_name: artist.name, artist_mbid: artist.id }),
      });
      if (res.ok) {
        const d = await res.json();
        setWatchlist(p => [d.item, ...p]);
        setArtistQuery(''); setArtistResults([]); setShowArtistSearch(false);
        toast.success(`Now watching ${artist.name}`);
      } else if (res.status === 409) toast.info(`${artist.name} already in watchlist`);
    } catch { toast.error('Failed to add artist'); }
  }, [userId]);

  const removeFromWatchlist = useCallback(async (artistName) => {
    const item = watchlist.find(w => w.artist_name.toLowerCase() === artistName.toLowerCase());
    if (!item) return;
    try {
      await fetch(`${API_BASE}/api/music-releases/watchlist/${item.id}?user_id=${encodeURIComponent(userId)}`, { method: 'DELETE' });
      setWatchlist(p => p.filter(w => w.id !== item.id));
      toast.success(`Removed ${artistName}`);
    } catch { toast.error('Failed to remove'); }
  }, [watchlist, userId]);

  // Movie favorites actions
  const addMovieToFavorites = useCallback(async (movie) => {
    if (!userId) { toast.error('Please log in'); return; }
    try {
      const res = await fetch(`${API_BASE}/api/movies/favorites?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          movie_id: movie.id,
          title: movie.title,
          poster: movie.poster,
          release_date: movie.release_date,
          rating: movie.rating,
          genres: movie.genres,
          overview: movie.overview,
        }),
      });
      if (res.ok) { 
        const d = await res.json(); 
        setMovieFavorites(p => [d.item, ...p]); 
        toast.success(`Added "${movie.title}" to favorites`); 
      }
      else if (res.status === 409) toast.info(`"${movie.title}" is already in favorites`);
    } catch { toast.error('Failed to add to favorites'); }
  }, [userId]);

  const removeMovieFromFavorites = useCallback(async (movie) => {
    if (!userId) return;
    try {
      await fetch(`${API_BASE}/api/movies/favorites/${movie.id}?user_id=${encodeURIComponent(userId)}`, { method: 'DELETE' });
      setMovieFavorites(p => p.filter(f => f.movie_id !== movie.id));
      toast.success(`Removed "${movie.title}" from favorites`);
    } catch { toast.error('Failed to remove'); }
  }, [userId]);

  // Build a favorites lookup set for O(1) checks
  const favoriteMovieIds = useMemo(
    () => new Set(movieFavorites.map(f => f.movie_id)),
    [movieFavorites]
  );

  const toggleGenre = (gid) => {
    if (gid === 'all') { setActiveGenres(['all']); return; }
    setActiveGenres(prev => {
      const without = prev.filter(g => g !== 'all');
      if (without.includes(gid)) {
        const next = without.filter(g => g !== gid);
        return next.length === 0 ? ['all'] : next;
      }
      return [...without, gid];
    });
  };

  // Build a watchlist lookup set for O(1) checks
  const watchedArtists = useMemo(
    () => new Set(watchlist.map(w => w.artist_name.toLowerCase())),
    [watchlist]
  );

  // Memoize filtered + grouped results
  const isAllGenres = activeGenres.includes('all') || activeGenres.length === 0;

  const { sortedDates, grouped, totalDisplayed } = useMemo(() => {
    const baseList = tab === 'watchlist'
      ? releases.filter(r => watchedArtists.has(r.artist.toLowerCase()))
      : releases;

    const q = search.toLowerCase();
    const filtered = baseList.filter(r => {
      if (q && !r.title.toLowerCase().includes(q) && !r.artist.toLowerCase().includes(q)) return false;
      if (!isAllGenres) {
        // Use r.genre (pre-classified from curated data) first, then fall back to genreMap (LLM-classified)
        const rGenre = r.genre || genreMap[r.artist.toLowerCase()];
        if (!rGenre || !activeGenres.includes(rGenre)) return false;
      }
      return true;
    });

    const grp = {};
    for (const r of filtered) {
      const k = r.release_date || 'TBA';
      if (!grp[k]) grp[k] = [];
      grp[k].push(r);
    }

    const dates = Object.keys(grp).sort(tab === 'recent' ? (a, b) => b.localeCompare(a) : undefined);
    return { sortedDates: dates, grouped: grp, totalDisplayed: filtered.length };
  }, [releases, tab, search, isAllGenres, activeGenres, genreMap, watchedArtists]);

  // Paginated flat list for infinite scroll
  const visibleReleases = useMemo(() => {
    const flat = [];
    for (const date of sortedDates) {
      for (const r of grouped[date]) {
        flat.push({ ...r, _groupDate: date });
        if (flat.length >= visibleCount) return flat;
      }
    }
    return flat;
  }, [sortedDates, grouped, visibleCount]);

  const visibleGrouped = useMemo(() => {
    const grp = {};
    for (const r of visibleReleases) {
      const k = r._groupDate;
      if (!grp[k]) grp[k] = [];
      grp[k].push(r);
    }
    return grp;
  }, [visibleReleases]);

  const visibleDates = useMemo(
    () => sortedDates.filter(d => visibleGrouped[d]),
    [sortedDates, visibleGrouped]
  );

  const hasMore = visibleReleases.length < totalDisplayed;

  // Movie filtering + grouping (separate from music)
  const { movieDates, movieGrouped, totalMovies } = useMemo(() => {
    const q = search.toLowerCase();
    const filtered = movies.filter(m => {
      if (q && !m.title.toLowerCase().includes(q)) return false;
      return true;
    });

    const grp = {};
    for (const m of filtered) {
      const k = m.release_date || 'TBA';
      if (!grp[k]) grp[k] = [];
      grp[k].push(m);
    }

    const dates = Object.keys(grp).sort(tab === 'recent' ? (a, b) => b.localeCompare(a) : undefined);
    return { movieDates: dates, movieGrouped: grp, totalMovies: filtered.length };
  }, [movies, search, tab]);

  const visibleMovies = useMemo(() => {
    const flat = [];
    for (const date of movieDates) {
      for (const m of movieGrouped[date]) {
        flat.push({ ...m, _groupDate: date });
        if (flat.length >= visibleCount) return flat;
      }
    }
    return flat;
  }, [movieDates, movieGrouped, visibleCount]);

  const visibleMovieGrouped = useMemo(() => {
    const grp = {};
    for (const m of visibleMovies) {
      const k = m._groupDate;
      if (!grp[k]) grp[k] = [];
      grp[k].push(m);
    }
    return grp;
  }, [visibleMovies]);

  const visibleMovieDates = useMemo(
    () => movieDates.filter(d => visibleMovieGrouped[d]),
    [movieDates, visibleMovieGrouped]
  );

  const hasMoreMovies = visibleMovies.length < totalMovies;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Top Bar */}
      <div className="sticky top-0 z-20 bg-black/95 backdrop-blur border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 pt-4 pb-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center">
                <Music size={18} className="text-purple-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Media Releases</h1>
                <p className="text-xs text-gray-500">
                  {loading ? 'Loading...' : mediaType === 'music' ? `${totalDisplayed} releases` : `${totalMovies} movies`}
                  {classifying && <span className="ml-2 text-purple-400 animate-pulse">classifying genres...</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Media Type Toggle */}
              <div className="flex items-center p-0.5 bg-gray-900 rounded-lg border border-white/10">
                <button onClick={() => { setMediaType('music'); setTab('upcoming'); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    mediaType === 'music' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
                  }`} data-testid="media-type-music">
                  <Music size={14} /> Music
                </button>
                <button onClick={() => { setMediaType('movies'); setTab('upcoming'); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    mediaType === 'movies' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'
                  }`} data-testid="media-type-movies">
                  <Film size={14} /> Movies
                </button>
              </div>
              <button onClick={() => mediaType === 'music' ? fetchReleases() : fetchMovies()} disabled={loading}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                data-testid="refresh-btn">
                <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              </button>
              {mediaType === 'music' && (
                <button onClick={() => setShowArtistSearch(v => !v)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm transition-colors"
                  data-testid="add-artist-btn">
                  <Plus size={15} /> Watch Artist
                </button>
              )}
            </div>
          </div>

          {showArtistSearch && (
            <div className="p-3 bg-black border border-gray-400 rounded-xl">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input autoFocus value={artistQuery} onChange={e => setArtistQuery(e.target.value)}
                  placeholder="Search for an artist to watch..."
                  className="w-full pl-8 pr-4 py-2 bg-black border border-white/20 rounded-lg text-sm text-white focus:outline-none focus:border-white/50"
                  data-testid="artist-search-input" />
                {artistSearching && <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 animate-spin" />}
              </div>
              {artistResults.length > 0 && (
                <div className="mt-2 space-y-0.5">
                  {artistResults.map(a => (
                    <button key={a.id} onClick={() => addArtistToWatchlist(a)}
                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-800 rounded-lg text-sm text-left transition-colors"
                      data-testid={`artist-result-${a.id}`}>
                      <span className="text-white">{a.name}</span>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {a.type && <span className="px-1.5 py-0.5 bg-gray-800 rounded">{a.type}</span>}
                        {a.country && <span>{a.country}</span>}
                        <Plus size={13} className="text-purple-400" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="flex items-center gap-1">
            {mediaType === 'music' ? (
              <>
                {[
                  { id: 'upcoming',  label: 'Upcoming Music',  icon: TrendingUp },
                  { id: 'recent',    label: 'Recent Music',    icon: Radio },
                  { id: 'watchlist', label: `Watchlist${watchlist.length ? ` (${watchlist.length})` : ''}`, icon: Bookmark },
                ].map(({ id, label, icon: Icon }) => (
                  <button key={id} onClick={() => setTab(id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      tab === id
                        ? 'bg-purple-600/20 text-purple-300 border border-purple-500/40'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`} data-testid={`tab-${id}`}>
                    <Icon size={13} />{label}
                  </button>
                ))}
              </>
            ) : (
              <>
                {[
                  { id: 'upcoming',  label: 'Upcoming Movies', icon: TrendingUp },
                  { id: 'recent',    label: 'Now Playing',     icon: Film },
                  { id: 'favorites', label: `Favorites${movieFavorites.length ? ` (${movieFavorites.length})` : ''}`, icon: Heart },
                ].map(({ id, label, icon: Icon }) => (
                  <button key={id} onClick={() => setTab(id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      tab === id
                        ? 'bg-red-600/20 text-red-300 border border-red-500/40'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`} data-testid={`tab-${id}`}>
                    <Icon size={13} />{label}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="border-b border-white/5 bg-black/80 sticky top-[130px] z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder={mediaType === 'music' ? 'Search releases or artists...' : 'Search movies...'}
                className="w-full pl-8 pr-3 py-1.5 bg-black border border-white/20 rounded-lg text-sm text-white focus:outline-none focus:border-white/50"
                data-testid="release-search" />
              {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"><X size={13} /></button>}
            </div>

            {mediaType === 'music' && (
              <>
                <div className="flex items-center gap-1">
                  {DATE_PRESETS.map(p => (
                    <button key={p.id} onClick={() => setDatePreset(p.id)}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        datePreset === p.id ? 'bg-purple-600/30 text-purple-300 border border-purple-500/40' : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                      }`} data-testid={`date-preset-${p.id}`}>
                      {p.label}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-0.5 p-0.5 bg-black rounded-lg border border-white/20">
                  {['all','album','single','ep'].map(t => (
                    <button key={t} onClick={() => setTypeFilter(t)}
                      className={`px-2 py-1 rounded text-xs capitalize font-medium transition-all ${
                        typeFilter === t ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-300'
                      }`} data-testid={`type-${t}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {mediaType === 'music' && (
            <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 scrollbar-hide">
              <Filter size={13} className="text-gray-600 flex-shrink-0" />
              {GENRES.map(g => {
                const active = isAllGenres ? g.id === 'all' : activeGenres.includes(g.id);
                return (
                  <button key={g.id} onClick={() => toggleGenre(g.id)}
                    className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-all border ${
                      active ? 'text-white border-transparent' : 'border-gray-800 text-gray-500 hover:text-gray-300 hover:border-gray-700'
                    }`}
                    style={active ? { backgroundColor: g.color + '35', borderColor: g.color + '60', color: g.color } : {}}
                    data-testid={`genre-${g.id}`}>
                    {g.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 max-w-6xl mx-auto w-full px-4 py-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <Loader2 size={32} className={`animate-spin ${mediaType === 'music' ? 'text-purple-400' : 'text-red-400'}`} />
            <p className="text-gray-400 text-sm">Loading {mediaType === 'music' ? 'releases' : 'movies'}...</p>
          </div>
        ) : mediaType === 'music' ? (
          /* ===== MUSIC CONTENT ===== */
          totalDisplayed === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <Music size={40} className="text-gray-700" />
              <p className="text-gray-400 text-sm">
                {tab === 'watchlist' && watchlist.length === 0
                  ? 'Add artists to your watchlist using "Watch Artist"'
                  : 'No releases match the current filters'}
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {visibleDates.map(date => (
                <div key={date}>
                  <div className="flex items-center gap-3 mb-3">
                    {(() => { const cd = countdown(date); return (
                      <div className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-semibold border ${
                        cd?.label === 'Today'    ? 'bg-purple-600/20 text-purple-300 border-purple-500/30' :
                        cd?.label === 'Tomorrow' ? 'bg-cyan-600/15 text-cyan-300 border-cyan-500/20' :
                        'bg-black text-gray-400 border-gray-800'
                      }`}>
                        <Calendar size={13} />
                        {fmt(date)}
                        {cd && <span className="text-xs opacity-70">{cd.label}</span>}
                      </div>
                    ); })()}
                    <div className="flex-1 h-px bg-gray-800/80" />
                    <span className="text-xs text-gray-600">{grouped[date]?.length || 0}</span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {(visibleGrouped[date] || []).map(r => (
                      <ReleaseCard
                        key={r.id}
                        release={r}
                        isWatched={watchedArtists.has(r.artist.toLowerCase())}
                        genreInfo={GENRE_MAP[r.genre] || GENRE_MAP[genreMap[r.artist.toLowerCase()]] || null}
                        onWatch={addToWatchlist}
                        onUnwatch={removeFromWatchlist}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {hasMore && (
                <div ref={sentinelRef} className="flex justify-center py-8">
                  <Loader2 size={20} className="animate-spin text-gray-600" />
                </div>
              )}
            </div>
          )
        ) : (
          /* ===== MOVIES CONTENT ===== */
          tab === 'favorites' ? (
            /* ===== FAVORITES TAB ===== */
            movieFavorites.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 gap-3">
                <Heart size={40} className="text-gray-700" />
                <p className="text-gray-400 text-sm">No favorite movies yet</p>
                <p className="text-gray-600 text-xs">Click the heart icon on any movie to add it to your favorites</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {movieFavorites.map(fav => (
                    <MovieCard
                      key={fav.id}
                      movie={{
                        id: fav.movie_id,
                        title: fav.title,
                        poster: fav.poster,
                        release_date: fav.release_date,
                        rating: fav.rating,
                        genres: fav.genres,
                        overview: fav.overview,
                      }}
                      isFavorite={true}
                      onFavorite={addMovieToFavorites}
                      onUnfavorite={(m) => removeMovieFromFavorites({ ...m, id: fav.movie_id })}
                      onCardClick={setSelectedMovie}
                    />
                  ))}
                </div>
              </div>
            )
          ) : totalMovies === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <Film size={40} className="text-gray-700" />
              <p className="text-gray-400 text-sm">No movies found</p>
            </div>
          ) : (
            <div className="space-y-8">
              {visibleMovieDates.map(date => (
                <div key={date}>
                  <div className="flex items-center gap-3 mb-3">
                    {(() => { const cd = countdown(date); return (
                      <div className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-semibold border ${
                        cd?.label === 'Today'    ? 'bg-red-600/20 text-red-300 border-red-500/30' :
                        cd?.label === 'Tomorrow' ? 'bg-orange-600/15 text-orange-300 border-orange-500/20' :
                        'bg-black text-gray-400 border-gray-800'
                      }`}>
                        <Calendar size={13} />
                        {fmt(date)}
                        {cd && <span className="text-xs opacity-70">{cd.label}</span>}
                      </div>
                    ); })()}
                    <div className="flex-1 h-px bg-gray-800/80" />
                    <span className="text-xs text-gray-600">{movieGrouped[date]?.length || 0}</span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {(visibleMovieGrouped[date] || []).map(m => (
                      <MovieCard 
                        key={m.id} 
                        movie={m} 
                        isFavorite={favoriteMovieIds.has(m.id)}
                        onFavorite={addMovieToFavorites}
                        onUnfavorite={removeMovieFromFavorites}
                        onCardClick={setSelectedMovie}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {hasMoreMovies && (
                <div ref={sentinelRef} className="flex justify-center py-8">
                  <Loader2 size={20} className="animate-spin text-gray-600" />
                </div>
              )}
            </div>
          )
        )}
      </div>

      {/* Watchlist float panel */}
      {mediaType === 'music' && tab === 'watchlist' && watchlist.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-black border border-gray-400 rounded-2xl p-4 w-60 shadow-2xl z-30">
          <p className="text-xs text-gray-400 mb-2 flex items-center gap-1.5">
            <Bookmark size={12} className="text-yellow-400" />
            {watchlist.length} artist{watchlist.length > 1 ? 's' : ''} watched
          </p>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {watchlist.map(w => (
              <div key={w.id} className="flex items-center justify-between">
                <span className="text-sm text-white truncate">{w.artist_name}</span>
                <button onClick={() => removeFromWatchlist(w.artist_name)}
                  className="p-1 text-gray-600 hover:text-red-400 flex-shrink-0" data-testid={`rm-${w.id}`}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Movie Detail Modal */}
      {selectedMovie && (
        <MovieDetailModal
          movie={selectedMovie}
          onClose={() => setSelectedMovie(null)}
          isFavorite={favoriteMovieIds.has(selectedMovie.id)}
          onFavorite={addMovieToFavorites}
          onUnfavorite={removeMovieFromFavorites}
        />
      )}
    </div>
  );
}
